import React, { useCallback, useEffect, useState, useReducer } from 'react'
import { useDispatch } from 'react-redux'
import effects from 'app/effects/effects'
import { useIntl } from 'react-intl'
import clsx from 'clsx'
import { usePlace } from 'commons/usePlace'
import MessageAlert from 'app/components/MessageAlert'
import useStyles from './styles'
import { exportBeautifulPDF } from 'app/utilize/generate-pdf'
import { exportCsv } from 'app/utilize/generate-csv'
import AccessControl from 'app/auth/AccessControl'
import { AccessType } from 'app/auth/accessControlService'

import DialogAddSubGroup from './components/dialogs/DialogAddSubGroup'
import DialogRenameGroup from './components/dialogs/DialogRenameGroup'
import DialogRenameSubgroup from './components/dialogs/DialogRenameSubgroup'
import DialogDeleteGroupDetails from './components/dialogs/DialogDeleteGroupDetails'
import DialogDeleteSubgroupsGroupDetails from './components/dialogs/DialogDeleteSubgroupsGroupDetails'
import DialogAttachSubscribers from './components/dialogs/DialogAttachSubscribers'
import DialogDetachSubscribers from './components/dialogs/DialogDetachSubscribers'
import DialogExportCSVGroupDetails from './components/dialogs/DialogExportCSVGroupDetails'
import DialogExportPDFGroupDetails from './components/dialogs/DialogExportPDFGroupDetails'
import GroupDetailsSubgroups from './components/GroupDetailsSubgroups'
import GroupDetailsLanguages from './components/GroupDetailsLanguages'
import GroupDetailsTable from './components/GroupDetailsTable'
import { isOrdinaryGroup } from 'commons/groupLogic'
import { useTableFetch, pageToSortPairs } from 'commons/reactTableFetch'
import DialogError from 'app/main/group/components/dialogs/DialogError'
import PrimaryButton from 'app/components/buttons/PrimaryButton'
import { Routes } from 'app/services/routeService/routeService'

import {
  GET_GROUP_FETCH_SUCCESS,
  GROUP_RENAME_FETCH_SUCCESS,
  ADD_SUBGROUP_FETCH_REQUEST,
  GROUP_RENAME_FETCH_REQUEST,
  RENAME_SUBGROUP_FETCH_REQUEST,
  DELETE_SUBGROUP_REQUEST,
  DELETE_GROUP_REQUEST,
  ATTACH_SUBSCRIBERS_REQUEST,
  GET_SUBSCRIBERS_REQUEST,
  DETACH_SUBSCRIBERS_REQUEST,
  DETACH_SUBSCRIBERS_GROUP_REQUEST,
  EDIT_SUBSCRIBERS_REQUEST,
} from './actions'

import reducer from './reducer'
import { isHomelessGroup, accessibleSubgroups } from 'commons/groupLogic'
import * as R from 'ramda'
import { usePermissionInfo } from 'app/auth/useAccessControl'
import { arrayAsHookDependencies } from 'libs/hook'
import DialogAddSubscribers from './components/dialogs/DialogAddSubscribers'
import { useConversationSocket } from 'app/main/messages/conversations/useConversationSocket'
import { SocketEvent } from 'app/main/messages/conversations/socket'

import crmLogic from '../subscriber/components/crm/crmLogic'
import CrmDialogsList from '../subscriber/components/crm/CrmDialogsList'
import {
  PLACE_INVITATION_ACCEPT_REQUEST,
  PLACE_INVITATION_ACCEPT_INVITATION,
  PLACE_INVITATION_PUBLIC_SUBSCRIBE,
} from 'app/configs/statusConfigs'
import { TYPE_GROUP, TYPE_SUBGROUP } from 'app/main/subscriber/constants'
import { SUB_GROUP } from 'app/constants/recipient'

const RENAME_GROUP_DIALOG = 'isOpenRenameGroupDialog'
const DELETE_GROUP_DIALOG = 'isOpenDeleteGroupDialog'
const ADD_SUB_GROUP_DIALOG = 'isOpenAddSubGroupDialog'
const RENAME_SUB_GROUP_DIALOG = 'isOpenRenameSubGroupDialog'
const DELETE_SUB_GROUP_DIALOG = 'isOpenDeleteSubGroupDialog'
const ATTACH_SUBSCRIBERS_DIALOG = 'isOpenAttachSubscribersDialog'
const DETACH_SUBSCRIBERS_DIALOG = 'isOpenDetachSubscribersDialog'
const EXPORT_PDF_DIALOG = 'isOpenExportCSVDialog'
const EXPORT_CSV_DIALOG = 'isOpenExportPDFDialog'
const EDIT_SUBSCRIBER_DIALOG = 'isOpenEditSubscribersDialog'

const ALL_SUBGROUPS = 'all_subgroups'

const withTagsId = R.over(
  R.lensProp('tags'),
  R.compose(
    R.map(R.prop('id')),
    R.filter(R.compose(isOrdinaryGroup, R.prop('group'))),
  ),
)

export default (props) => {
  const place = usePlace()
  const { id: placeId } = place
  const perPage = 1
  const limit = 10000
  const {
    match: {
      params: { groupId },
    },
    parentState,
    setParentState,
    setGroupList,
    setSubscriberList,
  } = props

  const classes = useStyles()
  const { formatMessage } = useIntl()
  const dispatch = useDispatch()
  const [rerenderPage, setRerenderPage] = useState('')
  const [openErrorDialog, setOpenErrorDialog] = useState(false)
  const [selectedGroup, setSelectedGroup] = useState(false)

  const [groupState, dispatchGroup] = useReducer(...reducer)
  const { groupDetails } = groupState
  const keyCount = Object.keys(groupDetails).length
  const { handleRecordUpdates } = useConversationSocket()
  const [isLoading, setIsLoading] = useState(false)

  const readOnly = isHomelessGroup(groupDetails)

  const getStatus = (status) => {
    if (status === -1) {
      return null
    } else {
      return status
    }
  }

  const onErrorViewSubscriber = () => {
    props.history.push(Routes.permissionDenied())
  }

  const fetchData = (page) => {
    const status = getStatus(state.filter)
    return effects.apis.subscribers({
      page: state.page + 1 || 1,
      limit: page.pageSize,
      sort: pageToSortPairs(page),
      keyword: page.keyword,
      status,
    })
  }
  const pageData = useTableFetch(fetchData)

  const initState = {
    [RENAME_GROUP_DIALOG]: false,
    [DELETE_GROUP_DIALOG]: false,
    [ADD_SUB_GROUP_DIALOG]: false,
    [RENAME_SUB_GROUP_DIALOG]: false,
    [DELETE_SUB_GROUP_DIALOG]: false,
    [ATTACH_SUBSCRIBERS_DIALOG]: false,
    [DETACH_SUBSCRIBERS_DIALOG]: false,
    [EXPORT_PDF_DIALOG]: false,
    [EXPORT_CSV_DIALOG]: false,
    [EDIT_SUBSCRIBER_DIALOG]: false,
    membersList: [],
    attachedMembers: [],
    subGroupName: '',
    subGroupId: '',
    deleteMsg: '',
    groupDetailsList: {},
    selectedSubgroup: {
      id: ALL_SUBGROUPS,
      name: undefined,
    },
    detachSubscribersInfo: {},
    subscribersCount: 0,
    selected: {},
  }
  const [state, setState] = useState(initState)

  const apiFetchersCallback = useCallback(
    (fetchType, params) => {
      let fetch = () => effects.apis.groupDetails({ groupId })
      if (fetchType === GROUP_RENAME_FETCH_REQUEST) {
        fetch = () => effects.apis.updateGroup(params)
      } else if (fetchType === ADD_SUBGROUP_FETCH_REQUEST) {
        fetch = () => effects.apis.addSubgroup(params)
      } else if (fetchType === RENAME_SUBGROUP_FETCH_REQUEST) {
        fetch = () => effects.apis.updateSubGroup(params)
      } else if (fetchType === DELETE_SUBGROUP_REQUEST) {
        fetch = () => effects.apis.deleteSubgroup(params)
      } else if (fetchType === DELETE_GROUP_REQUEST) {
        fetch = () => effects.apis.deleteGroups(params)
      } else if (fetchType === ATTACH_SUBSCRIBERS_REQUEST) {
        fetch = () => effects.apis.attachSubscribers(params)
      } else if (fetchType === GET_SUBSCRIBERS_REQUEST) {
        fetch = () => effects.apis.subscribersV2(params)
      } else if (fetchType === DETACH_SUBSCRIBERS_REQUEST) {
        fetch = () => effects.apis.detachSubscribers(params)
      } else if (fetchType === DETACH_SUBSCRIBERS_GROUP_REQUEST) {
        fetch = () => effects.apis.detachSubscribersFromGroup(params)
      } else if (fetchType === EDIT_SUBSCRIBERS_REQUEST) {
        fetch = () => effects.apis.updateSubscriber(params)
      }

      return fetch()
    },
    [groupId],
  )

  const { groups } = usePermissionInfo()

  const task = useCallback(async () => {
    try {
      const groupDataFetched = await apiFetchersCallback()
      const mapTypeInGroup = R.over(
        R.lensProp('tags'),
        accessibleSubgroups(groups),
        groupDataFetched,
      )

      dispatchGroup({
        type: GET_GROUP_FETCH_SUCCESS,
        data: {
          ...mapTypeInGroup,
          groupType: groupDataFetched.type,
          type: TYPE_GROUP,
        },
      })

      apiFetchersCallback(GET_SUBSCRIBERS_REQUEST, {
        placeId,
        perPage,
        limit,
      }).then(({ data }) => {
        if (
          selectedGroup &&
          selectedGroup.groupDetailsList &&
          selectedGroup.groupDetailsList.tags
        ) {
          setState((state) => ({
            ...state,
            ...selectedGroup,
            groupDetailsList: {
              name: selectedGroup.groupDetailsList.name,
              tags: selectedGroup.groupDetailsList.tags
                .map((tag) => {
                  return groupDataFetched.tags.find(
                    (tag2) => tag2.id === tag.subGroupId,
                  )
                })
                .filter((tag) => !!tag),
            },
            selectedSubgroup: selectedGroup.selectedSubgroup,
            membersList: data,
            selected: {},
            isSelectAll: false,
          }))
          setSelectedGroup({
            selectedSubgroup: selectedGroup.selectedSubgroup,
            groupDetailsList: {
              tags: selectedGroup.groupDetailsList.tags
                .map((tag) => {
                  const tagItem = groupDataFetched.tags.find(
                    (tag2) => tag2.id === tag.subGroupId,
                  )
                  return tagItem
                    ? {
                        subGroupId: tagItem.id,
                        name: tagItem.name,
                        groupId: tagItem.groupId,
                        subscribers: tagItem.subscribers,
                      }
                    : null
                })
                .filter((tag) => !!tag),
            },
          })
        } else {
          setState((state) => ({
            ...state,
            membersList: data,
          }))
        }
      })
      let changeSelectedGroup = false
      if (
        selectedGroup &&
        selectedGroup.selectedSubgroup &&
        selectedGroup.selectedSubgroup &&
        selectedGroup.selectedSubgroup.id
      ) {
        changeSelectedGroup =
          selectedGroup.selectedSubgroup.id !== ALL_SUBGROUPS
      }
      setParentState({
        ...parentState,
        selectedGroup: changeSelectedGroup,
      })
    } catch (error) {
      props.history.push('/groups')
    }
  }, [
    apiFetchersCallback,
    rerenderPage,
    placeId,
    arrayAsHookDependencies(groups),
  ])

  /* eslint-disable react-hooks/exhaustive-deps */
  useEffect(() => {
    task()
  }, [task])
  /* eslint-enable react-hooks/exhaustive-deps */

  useEffect(() => {
    let newStateObj = {
      groupDetailsList: groupDetails,
    }
    let count = new Set()
    groupDetails.tags &&
      groupDetails.tags.forEach((tag) => {
        tag['subscribers'] &&
          tag['subscribers'].forEach((subscriber) => {
            count.add(subscriber.id)
          })
        newStateObj['subscribersCount'] = count.size
      })
    if (
      selectedGroup &&
      selectedGroup.groupDetailsList &&
      selectedGroup.groupDetailsList.tags
    ) {
      setState((state) => ({
        ...state,
        ...selectedGroup,
        ...newStateObj,
        groupDetailsList: {
          ...newStateObj.groupDetailsList,
          name: selectedGroup.groupDetailsList.name,
          tags: selectedGroup.groupDetailsList.tags,
        },
        selectedSubgroup: selectedGroup.selectedSubgroup,
      }))
    } else {
      setState((state) => ({ ...state, ...newStateObj }))
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [groupDetails, setState])

  useEffect(() => {
    if (!R.isEmpty(groupDetails)) {
      const groupItem = R.mergeRight(groupDetails, {
        parentId: null,
        type: TYPE_GROUP,
      })

      const newSubGroup = R.compose(
        R.map((item) => {
          item.groupName = groupItem.name
          item.parentId = groupItem.id
          item.type = TYPE_SUBGROUP
          return item
        }),
        R.filter((tag) => {
          return R.compose(
            R.lt(0),
            R.length,
            R.filter((subscriber) =>
              R.includes(subscriber.lastInvitationStatus, [
                PLACE_INVITATION_ACCEPT_REQUEST,
                PLACE_INVITATION_ACCEPT_INVITATION,
                PLACE_INVITATION_PUBLIC_SUBSCRIBE,
              ]),
            ),
          )(tag.subscribers)
        }),
      )(groupItem.tags)

      setGroupList(newSubGroup.length > 0 ? [groupItem, ...newSubGroup] : [])
    }
  }, [groupDetails, setGroupList])

  function renderComponent() {
    setRerenderPage(new Date().getMilliseconds())
  }

  function toggleDialogs(dialogType, isOpen, tagItem = undefined) {
    if (tagItem) {
      if (
        [RENAME_SUB_GROUP_DIALOG, ATTACH_SUBSCRIBERS_DIALOG].includes(
          dialogType,
        )
      ) {
        state.subGroupName = tagItem.name
        state.subGroupId = tagItem.id
        state.attachedMembers = tagItem.subscribers.map(
          ({ id, firstName, lastName }) => {
            return {
              value: id,
              label: firstName + ' ' + lastName,
            }
          },
        )
      } else if (dialogType === DELETE_SUB_GROUP_DIALOG) {
        state.subGroupName = tagItem.name
        state.subGroupId = tagItem.id
        state.deleteMsg = `${formatMessage({
          id: 'group.details.dialog.deleteSubGroup',
        })}`
      } else if (dialogType === DETACH_SUBSCRIBERS_DIALOG) {
        let subscriberIds = Array.isArray(tagItem['subscriberId'])
          ? tagItem['subscriberId']
          : [tagItem['subscriberId']]

        let nameOfGroupOrSubGroup =
          state.selectedSubgroup['name'] || groupDetails['name']

        let detachSubscribersObj = {
          subscriberIds,
          deleteDialogTitle: formatMessage(
            { id: 'group.dialog.title1' },
            { subgroup: nameOfGroupOrSubGroup },
          ),
          deleteDialogDesc: formatMessage({
            id: 'group.dialog.desc2',
          }),
        }

        if (subscriberIds.length > 1) {
          detachSubscribersObj.deleteDialogTitle = formatMessage(
            { id: 'group.dialog.title2' },
            { subgroup: nameOfGroupOrSubGroup },
          )
          detachSubscribersObj.deleteDialogDesc = formatMessage({
            id: 'group.dialog.desc3',
          })
        }

        state.detachSubscribersInfo = {
          ...detachSubscribersObj,
          subGroupId: tagItem['subGroupId'],
        }
      } else if (dialogType === EDIT_SUBSCRIBER_DIALOG) {
        if (tagItem.countryCode) {
          state['editSubscriberData'] = tagItem
        } else {
          state['editSubscriberData'] = {
            ...tagItem,
            countryCode: '+1',
          }
        }
        state['editSubscriberData'] = withTagsId(state['editSubscriberData'])
      }
    }
    setState({
      ...state,
      [dialogType]: isOpen,
    })
  }

  function deleteGroup() {
    toggleDialogs(DELETE_GROUP_DIALOG, false)
    handleRecordUpdates(SocketEvent.UPDATE_GROUP, groupId, placeId)

    apiFetchersCallback(DELETE_GROUP_REQUEST, {
      groupIds: [groupId],
    }).then((data) => {
      props.history.push('/groups')
    })
  }

  function hendleAddSubGroup(requestData) {
    toggleDialogs(ADD_SUB_GROUP_DIALOG, false)
    requestData['groupId'] = groupId
    apiFetchersCallback(ADD_SUBGROUP_FETCH_REQUEST, requestData)
      .then((data) => {
        showSnackbar(formatMessage({ id: 'group.success_msg_1' }))
        renderComponent()
      })
      .catch((error) => {
        let { details } = error.response.data
        if (details) {
          dispatch(
            MessageAlert.showError({
              message: details['message'],
            }),
          )
        }
      })
  }

  function hendleUpdateName(requestData) {
    toggleDialogs(RENAME_GROUP_DIALOG, false)
    requestData['groupId'] = groupId
    apiFetchersCallback(GROUP_RENAME_FETCH_REQUEST, requestData)
      .then((data) => {
        handleRecordUpdates(SocketEvent.UPDATE_GROUP, groupId)

        dispatchGroup({
          type: GROUP_RENAME_FETCH_SUCCESS,
          data: requestData,
        })
      })
      .catch((error) => {
        let { details } = error.response.data
        if (details) {
          dispatch(
            MessageAlert.showError({
              message: details['message'],
            }),
          )
        }
      })
  }

  function hendleUpdateSubgroupName(requestData) {
    toggleDialogs(RENAME_SUB_GROUP_DIALOG, false)
    requestData['tagId'] = state.subGroupId
    apiFetchersCallback(RENAME_SUBGROUP_FETCH_REQUEST, requestData).then(
      (data) => {
        showSnackbar(formatMessage({ id: 'group.success_msg_2' }))
        renderComponent()
      },
    )
  }

  function hendleDeleteSubgroup() {
    toggleDialogs(DELETE_SUB_GROUP_DIALOG, false)
    apiFetchersCallback(DELETE_SUBGROUP_REQUEST, {
      tagId: state.subGroupId,
    }).then((data) => {
      handleRecordUpdates(SocketEvent.UPDATE_TAG, state.subGroupId, placeId)
      showSnackbar(formatMessage({ id: 'group.success_msg_3' }))
      renderComponent()
    })
  }

  function handleDetachSubscribers() {
    setIsLoading(true)
    toggleDialogs(DETACH_SUBSCRIBERS_DIALOG, false)

    let requestData = {
      subscriberIds: state.detachSubscribersInfo.subscriberIds,
    }

    let requestAction = ''
    if (!state.detachSubscribersInfo.subGroupId) {
      requestData['groupId'] = groupId
      requestAction = DETACH_SUBSCRIBERS_GROUP_REQUEST
    } else {
      requestData['tagId'] = state.detachSubscribersInfo.subGroupId
      requestAction = DETACH_SUBSCRIBERS_REQUEST
    }

    apiFetchersCallback(requestAction, requestData).then((data) => {
      showSnackbar(formatMessage({ id: 'group.success_msg_7' }))
      renderComponent()
      setIsLoading(false)
    })
  }

  async function handleAttachSubscribers(attachedMembers) {
    await setIsLoading(true)
    await setParentState({
      ...parentState,
      selectedGroup: state.selectedSubgroup.id !== ALL_SUBGROUPS,
    })
    let requestData = {
      subscriberIds: attachedMembers,
      tagId: state.selectedSubgroup.id,
    }
    await apiFetchersCallback(ATTACH_SUBSCRIBERS_REQUEST, requestData)
    setParentState({
      isOpenCreate: false,
    })
    renderComponent()
    setIsLoading(false)
    showSnackbar(formatMessage({ id: 'group.success_msg_4' }))
  }

  function showSnackbar(msg) {
    dispatch(MessageAlert.showSuccess({ message: msg }))
  }

  //Actually call when Click Tag (SubGroup)!!!!
  function onOpenSubscribers(tagItem) {
    let dataObj = {}
    if (tagItem === ALL_SUBGROUPS) {
      dataObj = {
        groupDetailsList: groupDetails,
        selectedSubgroup: {
          id: ALL_SUBGROUPS,
          name: tagItem.name,
        },
      }
    } else {
      dataObj = {
        groupDetailsList: {
          name: tagItem.name,
          tags: [
            {
              subGroupId: tagItem.id,
              name: tagItem.name,
              groupId: tagItem.groupId,
              subscribers: tagItem.subscribers,
            },
          ],
        },
        selectedSubgroup: {
          id: tagItem.id,
          name: tagItem.name,
        },
      }
    }

    setSelectedGroup(dataObj)

    const groupItem = R.mergeRight(groupDetails, {
      parentId: null,
      type: TYPE_GROUP,
    })

    if (dataObj.selectedSubgroup.id === ALL_SUBGROUPS) {
      const newSubGroup = R.compose(
        R.map((item) => {
          item.groupName = groupItem.name
          item.parentId = groupItem.id
          item.parentName = groupItem.name
          item.type = TYPE_SUBGROUP
          return item
        }),
        R.filter((tag) => {
          return R.compose(
            R.lt(0),
            R.length,
            R.filter((subscriber) =>
              R.includes(subscriber.lastInvitationStatus, [
                PLACE_INVITATION_ACCEPT_REQUEST,
                PLACE_INVITATION_ACCEPT_INVITATION,
                PLACE_INVITATION_PUBLIC_SUBSCRIBE,
              ]),
            ),
          )(tag.subscribers)
        }),
      )(groupItem.tags)
      setGroupList(newSubGroup.length > 0 ? [groupItem, ...newSubGroup] : [])
      setSubscriberList([])
    } else {
      const newSubGroup = R.compose(
        R.map((item) => {
          item.groupName = groupItem.name
          item.parentId = groupItem.id
          item.parentName = groupItem.name
          item.type = SUB_GROUP
          return item
        }),
        R.filter((tag) => {
          return (
            tag.id === dataObj.selectedSubgroup.id &&
            R.compose(
              R.lt(0),
              R.length,
              R.filter((subscriber) =>
                R.includes(subscriber.lastInvitationStatus, [
                  PLACE_INVITATION_ACCEPT_REQUEST,
                  PLACE_INVITATION_ACCEPT_INVITATION,
                  PLACE_INVITATION_PUBLIC_SUBSCRIBE,
                ]),
              ),
            )(tag.subscribers)
          )
        }),
      )(groupItem.tags)
      setSubscriberList([])
      setGroupList(newSubGroup.length > 0 ? newSubGroup : [])
    }

    setState({
      ...state,
      ...dataObj,
      selected: {},
      isSelectAll: false,
    })

    setParentState({
      ...parentState,
      selectedGroup: tagItem !== ALL_SUBGROUPS,
    })
  }

  function exportCSV() {
    toggleDialogs(EXPORT_CSV_DIALOG, false)

    let headerCVS = [
      {
        label: formatMessage({
          id: 'group.table.th.subscribers',
        }),
        key: 'subscriber',
      },
      {
        label: formatMessage({
          id: 'group.table.th.email',
        }),
        key: 'email',
      },
      {
        label: formatMessage({
          id: 'phone.label',
        }),
        key: 'phone',
      },
      {
        label: formatMessage({
          id: 'group.table.th.subgroup',
        }),
        key: 'subgroups',
      },
    ]

    exportCsv({
      headerCVS,
      dataCSV: getExportedData(),
      fileName: 'groups',
    })
  }

  function exportPDF() {
    toggleDialogs(EXPORT_PDF_DIALOG, false)
    let headerPDF = [
      {
        header: formatMessage({
          id: 'table.header.numberOfRow',
        }),
      },
      {
        header: formatMessage({
          id: 'group.table.th.subscribers',
        }),
      },
      {
        header: formatMessage({
          id: 'group.table.th.email',
        }),
      },
      {
        header: formatMessage({
          id: 'phone.label',
        }),
      },
      {
        header: formatMessage({
          id: 'group.table.th.subgroup',
        }),
      },
    ]
    const columnStyles = {
      0: { cellWidth: 40, halign: 'center' },
      1: { cellWidth: 150 },
      2: { cellWidth: 100 },
    }
    const headStyles = {
      halign: 'center',
    }

    exportBeautifulPDF({
      header: headerPDF,
      body: getExportedData(),
      columnStyles,
      headStyles,
      fileName: 'groups',
    })
  }

  function getExportedData() {
    let exportedDataArray = []
    let selectedKeys = Object.keys(state.selected)
    selectedKeys.forEach((value, index) => {
      exportedDataArray.push({
        no: index + 1,
        subscriber: `${state.selected[value]['firstName']} ${state.selected[value]['lastName']}`,
        email: state.selected[value]['email'],
        phone: state.selected[value]['phone'],
        subgroups: state.selected[value]['subgroups'],
      })
    })
    return exportedDataArray
  }

  const SUBSCRIBER_DETAILS_DIALOG = 'isOpenDetailsDialog'
  const SUBSCRIBER_EDIT_DETAILS_DIALOG = 'isOpenEditDetailsDialog'
  const SUBSCRIBER_MEMBERSHIP_CARD_DIALOG_DATA =
    'isOpenMembershipCardDialogData'
  const SUBSCRIBER_MEMBERSHIP_CARD_LIST_DIALOG_DATA =
    'isOpenMembershipCardListDialogData'
  const SUBSCRIBER_APPROVE_DIALOG = 'isOpenApproveDialog'
  const SUBSCRIBER_REJECT_DIALOG = 'isOpenRejectDialog'

  const [localState, setLocalState] = useState({
    subscriber: {},
    checkedItems: [],
    [SUBSCRIBER_DETAILS_DIALOG]: false,
    [SUBSCRIBER_EDIT_DETAILS_DIALOG]: false,
    [SUBSCRIBER_MEMBERSHIP_CARD_DIALOG_DATA]: undefined,
    [SUBSCRIBER_MEMBERSHIP_CARD_LIST_DIALOG_DATA]: undefined,
    [SUBSCRIBER_APPROVE_DIALOG]: false,
    [SUBSCRIBER_REJECT_DIALOG]: false,
  })

  let crmObj = crmLogic({
    localState,
    setLocalState,
  })

  return (
    <>
      <div className="flex flex-col flex-auto overflow-hidden">
        <div
          className={clsx(
            classes.cardWrapp,
            'flex flex-col flex-auto sm:flex sm:flex-row overflow-hidden',
          )}
        >
          <div className="flex flex-col">
            <GroupDetailsSubgroups
              groupName={groupDetails.name}
              onClickRename={() => {
                toggleDialogs(RENAME_GROUP_DIALOG, true)
              }}
              onClickCancelRename={() => {
                toggleDialogs(DELETE_GROUP_DIALOG, true)
              }}
              openErrorDialog={() => {
                setOpenErrorDialog(true)
              }}
              classes={classes}
              keyCount={keyCount}
              groupDetails={groupDetails}
              toggleDialogs={toggleDialogs}
              onOpenSubscribers={onOpenSubscribers}
              selectedSubgroup={state.selectedSubgroup}
              RENAME_SUB_GROUP_DIALOG={RENAME_SUB_GROUP_DIALOG}
              DELETE_SUB_GROUP_DIALOG={DELETE_SUB_GROUP_DIALOG}
              ATTACH_SUBSCRIBERS_DIALOG={ATTACH_SUBSCRIBERS_DIALOG}
              ADD_SUB_GROUP_DIALOG={ADD_SUB_GROUP_DIALOG}
              ALL_SUBGROUPS={ALL_SUBGROUPS}
              readOnly={readOnly}
            />

            <GroupDetailsLanguages classes={classes} state={state} />
          </div>
          <AccessControl
            accesses={[AccessType.ADMINISTRATION, AccessType.SUBSCRIBER_VIEW]}
          >
            <div
              className={clsx(
                classes.detailsTable,
                'flex flex-col flex-auto',
                'overflow-hidden',
              )}
            >
              <GroupDetailsTable
                classes={classes}
                state={state}
                setState={setState}
                setLocalState={setLocalState}
                toggleDialogs={toggleDialogs}
                DELETE_DIALOG={DELETE_SUB_GROUP_DIALOG}
                DETACH_SUBSCRIBERS_DIALOG={DETACH_SUBSCRIBERS_DIALOG}
                EXPORT_CSV_DIALOG={EXPORT_CSV_DIALOG}
                EXPORT_PDF_DIALOG={EXPORT_PDF_DIALOG}
                ALL_SUBGROUPS={ALL_SUBGROUPS}
                readOnly={readOnly}
                handleOpenSubscriber={crmObj.openOrCloseDetailsDialog}
                onErrorViewSubscriber={onErrorViewSubscriber}
                isLoading={isLoading}
              />
            </div>
          </AccessControl>
        </div>
      </div>

      <CrmDialogsList
        localState={localState}
        pageData={pageData}
        approveOrReject={crmObj.approveOrReject}
        openOrCloseDetailsDialog={crmObj.openOrCloseDetailsDialog}
        openOrCloseEditDetailsDialog={crmObj.openOrCloseEditDetailsDialog}
        updateCrmFields={crmObj.updateCrmFields}
        openMembershipCardDialog={crmObj.openMembershipCardDialog}
        closeMembershipCardDialog={crmObj.closeMembershipCardDialog}
        closeMembershipCardListDialog={crmObj.closeMembershipCardListDialog}
        openMembershipCardDetailDialog={crmObj.openMembershipCardDetailDialog}
        openMembershipCardListDialog={crmObj.openMembershipCardListDialog}
        isHideGroup={true}
      />

      <DialogAddSubGroup
        state={state}
        classes={classes}
        groupDetails={groupDetails}
        toggleDialogs={toggleDialogs}
        hendleAddSubGroup={hendleAddSubGroup}
        ADD_SUB_GROUP_DIALOG={ADD_SUB_GROUP_DIALOG}
      />

      <DialogRenameGroup
        state={state}
        classes={classes}
        groupDetails={groupDetails}
        hendleUpdateName={hendleUpdateName}
        toggleDialogs={toggleDialogs}
        RENAME_GROUP_DIALOG={RENAME_GROUP_DIALOG}
      />

      <DialogRenameSubgroup
        state={state}
        classes={classes}
        groupDetails={groupDetails}
        hendleUpdateSubgroupName={hendleUpdateSubgroupName}
        toggleDialogs={toggleDialogs}
        RENAME_SUB_GROUP_DIALOG={RENAME_SUB_GROUP_DIALOG}
      />

      <DialogDeleteGroupDetails
        state={state}
        classes={classes}
        groupDetails={groupDetails}
        hendleUpdateName={hendleUpdateName}
        toggleDialogs={toggleDialogs}
        deleteGroup={deleteGroup}
        DELETE_GROUP_DIALOG={DELETE_GROUP_DIALOG}
      />

      <DialogDeleteSubgroupsGroupDetails
        state={state}
        classes={classes}
        groupDetails={groupDetails}
        hendleUpdateName={hendleUpdateName}
        toggleDialogs={toggleDialogs}
        hendleDeleteSubgroup={hendleDeleteSubgroup}
        DELETE_SUB_GROUP_DIALOG={DELETE_SUB_GROUP_DIALOG}
      />

      <DialogAttachSubscribers
        state={state}
        setState={setState}
        classes={classes}
        groupDetails={groupDetails}
        hendleAttachSubscribers={handleAttachSubscribers}
        toggleDialogs={toggleDialogs}
        ATTACH_SUBSCRIBERS_DIALOG={ATTACH_SUBSCRIBERS_DIALOG}
      />

      <DialogDetachSubscribers
        state={state}
        setState={setState}
        classes={classes}
        handleDetachSubscribers={handleDetachSubscribers}
        toggleDialogs={toggleDialogs}
        DETACH_SUBSCRIBERS_DIALOG={DETACH_SUBSCRIBERS_DIALOG}
      />

      <DialogExportCSVGroupDetails
        state={state}
        classes={classes}
        toggleDialogs={toggleDialogs}
        exportCSV={exportCSV}
        EXPORT_CSV_DIALOG={EXPORT_CSV_DIALOG}
      />

      <DialogExportPDFGroupDetails
        state={state}
        classes={classes}
        toggleDialogs={toggleDialogs}
        exportPDF={exportPDF}
        EXPORT_PDF_DIALOG={EXPORT_PDF_DIALOG}
      />

      <DialogError
        isOpen={openErrorDialog}
        title={formatMessage({
          id: 'group.dialog.memberCard.delete.title',
        })}
        description={formatMessage({
          id: 'group.dialog.memberCard.delete.description',
        })}
        closeButton={
          <PrimaryButton
            labelId="action.ok"
            onClick={() => {
              setOpenErrorDialog(false)
            }}
          />
        }
      />

      {parentState.isOpenCreate && (
        <DialogAddSubscribers
          state={state}
          classes={classes}
          checkedItems={localState.checkedItems}
          groupDetails={groupDetails}
          groupDetailsList={state.groupDetailsList}
          groupId={groupId}
          toggleDialogs={() =>
            setParentState({
              ...parentState,
              isOpenCreate: false,
            })
          }
          handleAddSubscriber={handleAttachSubscribers}
          selectedSubgroup={state.selectedSubgroup}
          isOpen={parentState.isOpenCreate}
          isLoading={isLoading}
        />
      )}
    </>
  )
}
